import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase.js';

export default function Inbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function load() {
      const { data } = await supabase
        .from('inbox')
        .select('id, content, source, status, created_at')
        .eq('status', 'new')
        .order('created_at', { ascending: false });
      if (!mounted) return;
      setItems(data ?? []);
      setLoading(false);
    }
    load();

    const channel = supabase
      .channel('inbox-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'inbox' },
        () => load() // simple full-reload; cheap for inbox sizes
      )
      .subscribe();

    return () => {
      mounted = false;
      supabase.removeChannel(channel);
    };
  }, []);

  async function addItem(e) {
    e.preventDefault();
    const content = draft.trim();
    if (!content) return;
    setSubmitting(true);
    setDraft('');
    // optimistic prepend
    const tempId = `temp-${Date.now()}`;
    setItems((prev) => [
      { id: tempId, content, source: 'dashboard', status: 'new', created_at: new Date().toISOString() },
      ...prev,
    ]);
    const { error } = await supabase
      .from('inbox')
      .insert({ content, source: 'dashboard', status: 'new' });
    if (error) console.error(error);
    setSubmitting(false);
    // realtime channel will fix up the temp row
  }

  async function markDone(id) {
    setItems((prev) => prev.filter((it) => it.id !== id));
    await supabase.from('inbox').update({ status: 'done' }).eq('id', id);
  }

  return (
    <div className="bg-bg-card border border-border-base rounded p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs uppercase tracking-wider text-text-secondary">
          📥 Inbox
        </h3>
        {items.length > 0 && (
          <span className="text-[10px] px-2 py-0.5 bg-accent-amber/15 text-accent-amber rounded">
            {items.length}
          </span>
        )}
      </div>

      <form onSubmit={addItem} className="flex gap-2 mb-3">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Quick-add to inbox…"
          disabled={submitting}
          className="flex-1 px-3 py-1.5 bg-bg-input border border-border-base rounded text-sm text-text-primary focus:outline-none focus:border-accent-blue disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={!draft.trim() || submitting}
          className="px-3 py-1.5 bg-accent-blue text-white text-sm rounded hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          Add
        </button>
      </form>

      {loading ? (
        <div className="text-sm text-text-muted text-center py-3">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-text-muted text-center py-3">
          Inbox zero. ✨
        </div>
      ) : (
        <ul className="space-y-1.5 max-h-72 overflow-y-auto">
          {items.map((it) => (
            <li
              key={it.id}
              className="group flex items-start gap-2 px-2 py-1.5 hover:bg-bg-input/40 rounded transition"
            >
              <button
                onClick={() => markDone(it.id)}
                className="mt-0.5 w-4 h-4 rounded border border-border-base hover:bg-accent-green hover:border-accent-green flex items-center justify-center shrink-0 transition"
                aria-label="Mark done"
                title="Tap to mark done"
              />
              <span className="flex-1 text-sm text-text-primary">
                {it.content}
              </span>
              <span className="text-[10px] uppercase text-text-muted opacity-0 group-hover:opacity-100 transition">
                {it.source}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
